import AxiosInstance from '../config/AxiosInstance';

export interface Product {
    id: string;
    sku: string;
    type: number;
    productName: string;
    productGroup: { groupName: string };
    updatedAt: Date;
    isActive: boolean;
}


export interface OrderDetail {
    productId: string;
    orderQty: number;
    deliveryQty: number;
    varianceQty: number;
    cost: number;
    lineCost: number;
    product?: { productName: string };
}

export interface OrderDetailResponse {
    productId: string;
    orderQty: number;
    deliveryQty: number;
    varianceQty: number;
    cost: number;
    unitCost?: number;
    lineCost: number;
    currentStock: number;
    expectedQty?: number;
    partialQty?: number;
    id: string;
    product: {
        productName: string;
        sku: string;
        productGroup: {
            groupName: string;
        }
        UOM: {
            uomName: string;
        }
        productPrices: [{ cost: string, tax: { id: string } }];
    }
    orderTemplateDetail: {
        lowQty: number;
        medQty: number;
        highQty: number;
        partialDeliveriesDetails?: OrderDetailResponse[];
    }
    inTemplate?: boolean;
    uomName?: string
}

export interface PurchaseOrderDetail {
    clientId: string;
    client: { companyName: string };
    lineCost: number;
    internalNumber: number;
    companyId: string;
    comment: string;
    location: { name: string, id: string };
    statusId: number;
    amount: number;
    delivery: number;
    tax: number;
    orderTemplateId: string;
    orderTemplate: { templateName: string };
    orderDetails: OrderDetailResponse[];
    supplierReturn?: any;
    createdAt: string;
    approvedName?: string;
    approvalDate?: string;
    orderName?: string;
}
export interface PurchaseOrderPayload {
    clientId: string;
    internalNumber: number;
    companyId: string;
    comment: string;
    locationId: string;
    statusId: number;
    amount: number;
    delivery: number;
    tax: number;
    orderTemplateId: string;
    orderDetails: OrderDetail[];
}

const createOrders = async (payload: PurchaseOrderPayload) => {
    try {
        const { data } = await AxiosInstance.post('/api/inventory/orders', payload);
        return { data };
    } catch (error) {
        throw error;
    }
}

const updateOrders = async (id: string, payload: PurchaseOrderPayload) => {
    try {
        const { data } = await AxiosInstance.put('/api/inventory/orders/' + id, payload);
        return { data };
    } catch (error) {
        throw error;
    }
}

const getClients = async (page: any, limit: any, searchValue: any, companyId: any, clientType: any) => {
    try {
        const { data } = await AxiosInstance.get('/api/clients', {
            params: {
                page: page,
                limit: limit,
                searchValue: searchValue,
                clientType: clientType
            }
        });
        return { data };
    } catch (error) {
        throw error;
    }
};

const getListOrders = async (searchValue: string, type: string, page: string, limit: number) => {
    try {
        const { data } = await AxiosInstance.get('/api/inventory/orders/findAll', {
            params: {
                searchValue: searchValue,
                type: type,
                page: page,
                limit: limit,
            }
        });
        return { data };
    } catch (error) {
        throw error;
    }
}

const getDetailOrders = async (id: string) => {
    try {
        const { data } = await AxiosInstance.get(`/api/inventory/orders/find-one/${id}`);
        return { data };
    } catch (error) {
        throw error;
    }
}

const approveDetailOrders = async (id: string) => {
    try {
        const { data } = await AxiosInstance.put(`/api/inventory/orders/approve/${id}`);
        return { data };
    } catch (error) {
        throw error;
    }
}


// Until something is received; the lines stay on the cancelled order.
const cancelOrder = async (id: string) => {
    try {
        const { data } = await AxiosInstance.put(`/api/inventory/orders/cancel/${id}`);
        return { data };
    } catch (error) {
        throw error;
    }
}

const rejectDetailOrders = async (id: string) => {
    try {
        const { data } = await AxiosInstance.put(`/api/inventory/orders/reject/${id}`);
        return { data };
    } catch (error) {
        throw error;
    }
}


const deleteOrders = async (id: string) => {
    try {
        const { data } = await AxiosInstance.delete('/api/inventory/orders/delete/' + id);
        return { data };
    } catch (error) {
        throw error;
    }
}


export interface OrdersOverviewQuery {
    /** 'deliveries': only sent to received orders, by sent date, with what is still to receive. */
    view?: 'deliveries';
    from?: string;
    to?: string;
    status?: string;
    search?: string;
    supplierId?: string;
    locationId?: string;
    page: number;
    limit: number;
}

export interface OrdersOverviewRow {
    id: string;
    internalNumber: number;
    statusId: number;
    supplier: { id: string; name: string | null } | null;
    template: { id: string; name: string } | null;
    location: { id: string; name: string | null } | null;
    createdAt: string;
    updatedAt: string;
    createdBy: { id: string; name: string | null } | null;
    sentAt: string | null;
    sentBy: { id: string; name: string | null } | null;
    total: number;
    lines: number;
    /** Lines with everything ordered received. */
    linesReceived: number;
}

export interface StillToReceive {
    orders: number;
    lines: number;
    value: number;
    bySupplier: { supplierId: string; name: string | null; orders: number; lines: number; value: number }[];
}

export interface OrdersOverview {
    rows: OrdersOverviewRow[];
    count: number;
    page: number;
    limit: number;
    statusCounts: Record<string, number>;
    bySupplier: { supplierId: string; name: string | null; orders: number; total: number }[];
    totals: { orders: number; amount: number };
    viewer: { canApprove: boolean };
    /** Deliveries view only. */
    stillToReceive?: StillToReceive;
}

const getOrdersOverview = async (query: OrdersOverviewQuery) => {
    try {
        const { data } = await AxiosInstance.get('/api/inventory/orders/overview', { params: query });
        return { data };
    } catch (error) {
        throw error;
    }
}

export interface ReceiveOrderPayload {
    lines: { orderDetailId: string; quantity: number; unitPrice?: number }[];
    deliveryNoteNo?: string;
    note?: string;
    close?: boolean;
}

/** One receipt: what arrived now per line, in the line's unit and at the price paid per unit. */
const receiveOrder = async (id: string, payload: ReceiveOrderPayload) => {
    try {
        const { data } = await AxiosInstance.post(`/api/inventory/orders/${id}/receipts`, payload);
        return { data };
    } catch (error) {
        throw error;
    }
}

export {
    getClients,
    createOrders,
    updateOrders,
    getListOrders,
    getDetailOrders,
    deleteOrders,
    approveDetailOrders,
    rejectDetailOrders,
    cancelOrder,
    getOrdersOverview,
    receiveOrder
}